import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  TrendingUp,
  Users,
  Building2,
  Zap,
  ShieldCheck,
  ShieldAlert,
  Clock,
  Eye,
  Globe,
  Lock,
} from 'lucide-react';

const OsintGlobalAudit = () => {
  const navigate = useNavigate();

  const springTransition = {
    type: 'spring',
    stiffness: 100,
    damping: 20,
    mass: 1,
  };

  const objective =
    "Identifier toutes les données et informations sensibles accessibles publiquement sur internet afin de cartographier et de réduire votre surface d'exposition numérique.";

  const whatWeDo = [
    'Analyse des données publiques accessibles et informations sensibles exposées',
    "Cartographie de l’empreinte numérique globale et des présences en ligne",
    "Recherche d’identifiants compromis et données issues de fuites publiques",
    "Corrélation des informations collectées afin d’identifier les risques d’exploitation",
  ];

  const exposedSurface = [
    'Adresses emails professionnelles et structures nominatives',
    'Empreinte et présence sur les réseaux sociaux (dirigeants & collaborateurs)',
    "Identifiants et mots de passe compromis issus de fuites de données",
    'Métadonnées de documents publics (liaisons internes, versions de logiciels)',
    'Sous-domaines, infrastructures visibles et serveurs exposés',
    "Données d'enregistrement de marques et indexation moteurs de recherche",
  ];

  const identifiedRisks = [
    "Exposition d’identifiants compromis dans des bases de données publiques",
    "Collecte d’informations sensibles exploitables par ingénierie sociale",
    "Exposition involontaire de l'architecture des systèmes internes",
    "Augmentation drastique du risque de phishing ciblé et d’usurpation d’identité",
  ];

  const possibleConsequences = [
    {
      title: 'Phishing ultra-ciblé (Spear-phishing)',
      desc: "Création d'attaques par email sur-mesure et hautement crédibles basées sur l'historique des collaborateurs.",
    },
    {
      title: "Usurpation d'identité",
      desc: "Utilisation des données de l'entreprise ou des dirigeants pour mener des escroqueries (ex: fraude au président).",
    },
    {
      title: 'Compromission de comptes',
      desc: 'Accès immédiat à vos outils cloud ou infrastructures via la réutilisation de mots de passe fuités.',
    },
    {
      title: 'Atteinte à la réputation',
      desc: "Mise en évidence publique de la négligence de l'organisation concernant la gestion de ses données.",
    },
  ];

  const deliverables = [
    'Rapport détaillé des informations exposées publiquement',
    'Évaluation précise du niveau de criticité et des risques associés',
    "Captures d’écran, preuves factuelles et éléments de corrélation",
    "Plan de réduction de l'exposition numérique et recommandations prioritaires",
  ];

  const pricing = [
    {
      category: 'Particulier',
      range: '149€ – 199€',
      bgColor: 'bg-white',
      borderAccent: 'border-t-4 border-t-blue-500',
      icon: <Users size={20} className="text-blue-500" />,
    },
    {
      category: 'Particulier Premium',
      range: '290€ – 490€',
      bgColor: 'bg-white',
      borderAccent: 'border-t-4 border-t-slate-500',
      icon: <Zap size={20} className="text-slate-500" />,
    },
    {
      category: 'Petite entreprise',
      range: '900€ – 1500€',
      bgColor: 'bg-white',
      borderAccent: 'border-t-4 border-t-blue-500',
      icon: <Building2 size={20} className="text-blue-500" />,
    },
    {
      category: 'PME',
      range: '1500€ – 3000€',
      bgColor: 'bg-white',
      borderAccent: 'border-t-4 border-t-slate-500',
      icon: <TrendingUp size={20} className="text-slate-500" />,
    },
  ];

  return (
    <section className="w-full bg-[#f8fafc] py-24 px-6 flex flex-col items-center font-inter relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[300px] bg-gradient-to-b from-blue-500/5 to-transparent blur-3xl pointer-events-none" />

      <div className="text-center mb-20 max-w-4xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50/80 border border-blue-100 mb-6 backdrop-blur-sm shadow-sm"
        >
          <span className="text-[#1e3a5f] text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-blue-600" /> Renseignements & Cyber-Sûreté
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-black text-[#0f172a] mb-6 tracking-tight leading-tight"
        >
          Audit de Surface d’Exposition Numérique
        </motion.h2>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="p-4 bg-slate-900 border-l-4 border-blue-500 rounded-r-xl max-w-2xl mx-auto inline-block text-left shadow-md"
        >
          <p className="text-white text-xs md:text-sm font-bold flex items-center gap-2">
            <Lock size={16} className="text-blue-400 flex-shrink-0" /> Engagement de Confidentialité :
            <span className="font-medium text-slate-300">
              Toutes les données collectées durant cet audit passif sont traitées de manière strictement
              confidentielle et ne sont jamais partagées.
            </span>
          </p>
        </motion.div>
      </div>

      <div className="w-full max-w-[1050px] space-y-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springTransition}
            whileHover={{ y: -4, borderColor: '#bfdbfe', boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.05)' }}
            className="lg:col-span-5 p-8 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex flex-col justify-center transition-all duration-300"
          >
            <h3 className="text-lg font-black text-[#0f172a] mb-3 flex items-center gap-2.5">
              <Eye className="text-blue-600" size={20} /> Objectif de l'Audit
            </h3>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed font-medium">{objective}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springTransition, delay: 0.1 }}
            whileHover={{ y: -4, borderColor: '#e2e8f0', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.02)' }}
            className="lg:col-span-7 p-8 rounded-2xl bg-white border border-slate-200/80 shadow-sm transition-all duration-300"
          >
            <h3 className="text-lg font-black text-[#0f172a] mb-4 flex items-center gap-2.5">
              <Globe className="text-slate-500" size={20} /> Contexte de menace
            </h3>
            <div className="space-y-3 text-slate-600 text-sm font-medium leading-relaxed">
              <p>
                Les cyberattaques modernes commencent presque systématiquement par une phase de reconnaissance passive
                sur les sources ouvertes (OSINT).
              </p>
              <p>
                Avant toute intrusion, les attaquants collectent les informations publiques sur vos collaborateurs, vos
                technologies ou vos fuites historiques pour bâtir un scénario sur-mesure.
              </p>
              <p className="text-slate-900 font-bold bg-slate-50 p-3 rounded-lg border-l-2 border-slate-400 text-xs md:text-sm">
                Fait : Une exposition numérique excessive facilite fortement les campagnes d’ingénierie sociale et les
                attaques ciblées.
              </p>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-4">
          <div className="lg:col-span-6 space-y-4">
            <h3 className="text-xl font-black text-[#0f172a] mb-2">Ce que nous faisons concrètement</h3>
            <div className="grid grid-cols-1 gap-3">
              {whatWeDo.map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ x: 4, borderColor: '#bfdbfe' }}
                  className="flex items-center gap-4 p-4 rounded-xl border border-slate-200/80 bg-white shadow-sm transition-all duration-200"
                >
                  <div className="p-2 rounded-lg bg-blue-50 text-blue-600 flex-shrink-0">
                    <CheckCircle2 size={18} />
                  </div>
                  <span className="font-bold text-[#1e3a5f] text-xs md:text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 space-y-4">
            <h3 className="text-xl font-black text-[#0f172a] mb-2">Surface d'exposition analysée</h3>
            <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm space-y-3.5">
              {exposedSurface.map((surface, idx) => (
                <div key={idx} className="flex items-start gap-3 text-slate-600 text-xs md:text-sm font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                  <span>{surface}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
          <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
            <h3 className="text-lg font-black text-[#0f172a] mb-4 flex items-center gap-2">
              <AlertCircle className="text-orange-500" size={18} /> Fuites & Expositions détectées
            </h3>
            <div className="space-y-3.5">
              {identifiedRisks.map((risk, idx) => (
                <div key={idx} className="flex items-start gap-2.5 text-slate-600 text-xs md:text-sm font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                  <span>{risk}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-red-50/40 p-6 rounded-2xl border border-red-100 shadow-sm">
            <h3 className="text-lg font-black text-red-900 mb-4 flex items-center gap-2">
              <ShieldAlert className="text-red-600" size={18} /> Conséquences business potentielles
            </h3>
            <div className="space-y-4">
              {possibleConsequences.map((item, idx) => (
                <div key={idx} className="text-xs md:text-sm">
                  <h4 className="font-bold text-red-900 mb-0.5">{item.title}</h4>
                  <p className="text-red-900/70 font-medium text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 rounded-3xl bg-[#1e3a5f] text-white relative overflow-hidden shadow-xl border border-white/5"
        >
          <div className="relative z-10">
            <h3 className="text-xl font-black mb-6 flex items-center gap-3">
              <FileText size={22} className="text-blue-300" /> Livrables et Plan d'Action d'Exposition
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {deliverables.map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                  className="flex items-start gap-3 bg-white/10 p-4 rounded-xl border border-white/5 backdrop-blur-sm transition-colors duration-200"
                >
                  <FileText size={18} className="text-blue-300 mt-0.5 flex-shrink-0" />
                  <span className="font-bold text-xs md:text-sm leading-snug">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 blur-[100px] rounded-full -mr-20 -mt-20" />
        </motion.div>

        <div className="pt-4 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            <div className="lg:col-span-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-center">
              <h3 className="text-base font-black text-[#0f172a] mb-3 flex items-center gap-2">
                <Clock className="text-orange-500" size={16} /> Pourquoi agir maintenant ?
              </h3>
              <div className="space-y-3 text-xs text-slate-500 font-medium leading-relaxed">
                <p>
                  Les informations publiquement accessibles évoluent en permanence et sont exploitées à la vitesse
                  supérieure par les groupes cybercriminels.
                </p>
                <p className="text-orange-900 font-bold bg-orange-50/60 p-3 rounded-lg border-l-2 border-orange-400">
                  Une fuite d'identifiants ou une exposition critique d'un employé clé reste invisible pour vos équipes
                  sans un audit ciblé régulier.
                </p>
              </div>
            </div>

            <div className="lg:col-span-8 flex flex-col justify-between">
              <h3 className="text-[22px] font-black text-[#0f172a] mb-4">Grille des tarifs</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
                {pricing.map((plan, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ y: -4, boxShadow: '0 12px 20px rgba(30, 58, 95, 0.08)', borderColor: '#bfdbfe' }}
                    className={`p-5 rounded-xl ${plan.borderAccent} border border-slate-200 bg-white flex flex-col justify-between transition-all duration-300`}
                  >
                    <div>
                      <div className="mb-3 p-1.5 rounded-lg bg-slate-50 inline-block">{plan.icon}</div>
                      <h4 className="font-black text-[#0f172a] text-xs mb-1">{plan.category}</h4>
                      <p className="text-[18px] font-black text-[#1e3a5f]">{plan.range}</p>
                    </div>

                    <button
                      onClick={() => navigate('/rdv')}
                      className="w-full mt-4 py-2 rounded-lg bg-[#1e3a5f] text-white font-bold text-xs hover:bg-[#0f172a] transition-colors"
                    >
                      Demander un devis
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 pt-2 text-[11px] text-slate-400 font-bold">
            <span className="flex items-center gap-1">Protection des données à 100%</span>
            <span className="flex items-center gap-1">
              Les investigations OSINT sont réalisées exclusivement à partir de sources ouvertes légalement accessibles
              et sans interaction intrusive avec les systèmes ciblés.
            </span>
            <br />
            <span className="flex items-center gap-1">Le Plan de remédiation inclus</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OsintGlobalAudit;
